import { useCallback, useEffect, useRef, useState } from 'react';

const MediaRecorder = global.MediaRecorder;

export const Status = {
  IDLE: 'idle',
  ACQUIRING_STREAM: 'acquiring_stream',
  READY: 'ready',
  RECORDING: 'recording',
  STOPPING: 'stopping',
  STOPPED: 'stopped',
};

function ensureMediaPermissions() {
  return navigator.mediaDevices
    .enumerateDevices()
    .then(function (devices) {
      return devices.every(function (device) {
        return !(device.deviceId && device.label);
      });
    })
    .then(function (shouldAskForMediaPermissions) {
      if (shouldAskForMediaPermissions) {
        return navigator.mediaDevices
          .getUserMedia({ audio: true, video: true })
          .then(function (mediaStream) {
            mediaStream.getTracks().forEach(function (track) {
              track.stop();
            });
          });
      }
    });
}

function getDevicesOfKind(deviceInfos, kind) {
  return deviceInfos.filter(function (deviceInfo) {
    return deviceInfo.kind === kind;
  });
}

function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  if (MediaRecorder.isTypeSupported('video/mp4')) return 'video/mp4';
  if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9'))
    return 'video/webm;codecs=vp9';
  if (MediaRecorder.isTypeSupported('video/webm')) return 'video/webm';
  return '';
}

export const useMediaRecorder = ({
  audioOnly = false,
  onStartRecording = () => {},
  onStopRecording = () => {},
}) => {
  const [status, setStatus] = useState('idle');
  const [err, setErr] = useState(null);
  const [ref, setRef] = useState(null);
  const [isSupported, setIsSupported] = useState(undefined);
  const [mimeType, setMimeType] = useState();
  const [blobUrl, setBlobUrl] = useState(null);
  const mediaChunks = useRef([]);
  const stream = useRef();
  const recorder = useRef();

  useEffect(() => {
    if (ref && status === Status.IDLE) {
      init(getDeviceSettings());
      return () => {
        if (ref && ref.srcObject) {
          removeTracks(ref.srcObject);
          ref.srcObject = null;
          ref.captureStream = null;
        }
        if (stream.current) {
          getTracks(stream.current).forEach((track) => track.stop());
          stream.current = null;
        }
      };
    }
  }, [ref, status]);

  const setCaptureRef = useCallback(
    (captureRef) => {
      setRef(captureRef);
    },
    [stream],
  );

  const setStreamToVideoElement = (stream, videoEl) => {
    if (stream && videoEl) {
      videoEl.srcObject = stream;
      videoEl.captureStream = videoEl.captureStream || videoEl.mozCaptureStream;
    }
  };

  const init = useCallback(
    async (devices) => {
      setStatus(Status.ACQUIRING_STREAM);

      if (!MediaRecorder) {
        setErr('Unsupported browser');
        setStatus(Status.IDLE);
        setIsSupported(false);
        return;
      }

      let constraints = { audio: true };
      if (devices?.audioinput) {
        constraints.audio = { deviceId: devices.audioinput };
      }

      if (!audioOnly) {
        constraints.video = {
          facingMode: 'user',
          aspectRatio: { ideal: 1.7778 },
        };
        if (devices?.videoinput) {
          constraints.video.deviceId = devices.videoinput;
        }
      }
      try {
        stream.current = await navigator.mediaDevices.getUserMedia(constraints);
        setStreamToVideoElement(stream.current, ref);
        setStatus(Status.READY);
      } catch (err) {
        console.error('Error accessing media devices.', err);
        setErr(getRecorderError(err));
      }
    },
    [ref],
  );

  const onSaveDevices = (s) => {
    saveDeviceSettings(s);
    setStatus(Status.IDLE);
  };

  const startRecording = async () => {
    setErr(null);
    const t = getSupportedMimeType();
    if (!t) {
      setErr('No supported MIME type found for recording');
      return;
    }
    setMimeType(t);

    // Safari might not support captureStream — use original stream
    const targetStream =
      ref.captureStream?.() || ref.mozCaptureStream?.() || stream.current;

    if (!targetStream) {
      setErr('Cannot access stream for recording.');
      return;
    }

    const mediaRecorder = new MediaRecorder(targetStream, { mimeType });
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) mediaChunks.current.push(e.data);
    };
    mediaRecorder.onerror = (e) => {
      console.error('MediaRecorder error:', e);
      setErr(getRecorderError(e.error));
      setStatus(Status.IDLE);
    };
    mediaRecorder.onstop = onRecordingStop;
    mediaRecorder.start();
    recorder.current = mediaRecorder;
    setStatus(Status.RECORDING);
    onStartRecording();
  };

  const onRecordingStop = () => {
    const chunks = mediaChunks.current;
    const blob = new Blob(chunks, {
      type: chunks[0].type,
    });
    const url = URL.createObjectURL(blob);
    setStatus('stopped');
    setBlobUrl(url);

    recorder.current.stop();
    mediaChunks.current = [];
    mediaChunks.current = [];
    getTracks(stream.current).forEach((track) => track.stop());
    // stream.current = null;

    // Stop tracks after recording has fully stopped
    // if (stream.current) {
    //   getTracks(stream.current).forEach((track) => track.stop());
    //   stream.current = null;
    // }
    onStopRecording(url, blob);
  };

  const stopRecording = () => {
    if (recorder.current) {
      if (recorder.current.state !== 'inactive') {
        setStatus('stopping');
        recorder.current.stop();
      }
    }
  };

  const clearBlobUrl = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }
    setBlobUrl(undefined);
    setStatus(Status.IDLE);
  };

  return {
    status,
    setCaptureRef,
    setDevices: onSaveDevices,
    err,
    isSupported,
    mimeType,
    clearBlobUrl,
    blobUrl,
    startRecording,
    stopRecording,
    init,
  };
};

function getTracks(stream) {
  return stream.getTracks();
}

function removeTrack(stream, track) {
  track.stop();
  stream.removeTrack(track);
}

function removeTracks(stream) {
  try {
    const tracks = getTracks(stream);
    if (tracks && tracks.length > 0) {
      tracks.forEach((track) => removeTrack(stream, track));
    }
  } catch (err) {
    console.error(`Error while removing tracks: ${err.message}`);
  }
}

function getRecorderError(error) {
  switch (error.name) {
    case 'InvalidStateError':
      return 'An error occurred while recording. Try again later.';
    case 'NotAllowedError':
      return 'Camera access denied. Please allow access to record video';
    case 'SecurityError':
      return 'Recording is not allowed due to security restrictions.';
    default:
      return error.message;
  }
}

export function saveDeviceSettings(data) {
  try {
    localStorage.setItem('device_settings', JSON.stringify(data));
  } catch (e) {
    console.log(e);
  }
}

export function getDeviceSettings() {
  try {
    const d = localStorage.getItem('device_settings');
    return d ? JSON.parse(d) : null;
  } catch (e) {
    console.log(e);
  }
}

export function getDeviceSelectionOptions() {
  // before calling enumerateDevices, get media permissions (.getUserMedia)
  // w/o media permissions, browsers do not return device Ids and/or labels.
  return ensureMediaPermissions().then(function () {
    return navigator.mediaDevices
      .enumerateDevices()
      .then(function (deviceInfos) {
        const kinds = ['audioinput', 'videoinput'];
        return kinds.reduce(function (deviceSelectionOptions, kind) {
          deviceSelectionOptions[kind] = getDevicesOfKind(deviceInfos, kind);
          return deviceSelectionOptions;
        }, {});
      });
  });
}
